import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useGameLoop } from '../Shared/hooks/useGameLoop';
import { useAudio } from '../Shared/hooks/useAudio';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import { Link } from 'react-router-dom';

const PHASE = {
    INTRO: 'intro',
    MOUSE_INSTR: 'mouse_instr',
    MOUSE_TEST: 'mouse_test',
    LASER_INSTR: 'laser_instr',
    LASER_TEST: 'laser_test',
    RESULTS: 'results'
};

const ROUNDS_TO_COMPLETE = 10;

const TestLatency = () => {
    const canvasRef = useRef(null);
    const { beep, ping } = useAudio();

    // State
    const [phase, setPhase] = useState(PHASE.INTRO);
    const [lengthMode, setLengthMode] = useState('long'); // 'short' (5) or 'long' (10)
    const [mouseTimes, setMouseTimes] = useState([]);
    const [laserTimes, setLaserTimes] = useState([]);
    const [savedStats, setSavedStats] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [manualInputs, setManualInputs] = useState({ mouse: '', laser: '' });

    useEffect(() => {
        const m = localStorage.getItem('latencyMouseAvg');
        const l = localStorage.getItem('latencyLaserAvg');
        const diff = localStorage.getItem('calibratedLatency');
        if (m && l && diff) {
            setSavedStats({ mouse: Number(m), laser: Number(l), diff: Number(diff) });
        }
    }, []);

    // Trial State
    const stateRef = useRef({
        waitingForGreen: false, // true if we are in the random waiting period
        greenTime: 0,           // time when it turned green
        isGreen: false,         // visible state
        nextStimulusTime: 0,    // scheduled time for green
        earlyClicks: 0,
        cooldownUntil: 0 // ms timestamp before next input allowed
    });

    const [message, setMessage] = useState('');
    const [progress, setProgress] = useState(0); // Current trial number (1-10)

    // Reset for a fresh run
    const resetTests = useCallback(() => {
        setMouseTimes([]);
        setLaserTimes([]);
        setPhase(PHASE.INTRO);
        setProgress(0);
        stateRef.current = {
            waitingForGreen: false,
            greenTime: 0,
            isGreen: false,
            nextStimulusTime: 0,
            earlyClicks: 0,
            cooldownUntil: 0
        };
    }, []);

    const getRounds = () => lengthMode === 'short' ? 5 : 10;

    const startPhase = (newPhase) => {
        setPhase(newPhase);
        setProgress(1); // Round 1
        scheduleStimulus();
    };

    const scheduleStimulus = (lastRt = null) => {
        const state = stateRef.current;
        state.isGreen = false;
        state.waitingForGreen = true;
        // Random delay between 2s and 5s
        const delay = 1500 + Math.random() * 2500;
        state.nextStimulusTime = performance.now() + delay;

        let msg = "Wait for Green...";
        if (lastRt !== null) msg = `${lastRt.toFixed(0)}ms! ` + msg;
        setMessage(msg);
    };

    const handleMisclick = () => {
        const state = stateRef.current;
        if (state.waitingForGreen) {
            // False start!
            setMessage("Too Early! Wait for Green.");
            beep(200, 0.3); // Error buzz
            // Reschedule
            state.nextStimulusTime = performance.now() + 2000 + Math.random() * 3000;
        }
    };

    const handleCalibratedClick = () => {
        const state = stateRef.current;
        const now = performance.now();

        if (state.isGreen) {
            // Good click
            ping();
            const rt = now - state.greenTime;
            state.isGreen = false;
            state.waitingForGreen = false;

            if (phase === PHASE.MOUSE_TEST) {
                const newTimes = [...mouseTimes, rt];
                setMouseTimes(newTimes);

                // Debounce success click for 500ms
                state.cooldownUntil = now + 500;

                if (newTimes.length >= getRounds()) {
                    setPhase(PHASE.LASER_INSTR);
                } else {
                    setProgress(newTimes.length + 1);
                    scheduleStimulus(rt);
                }
            } else if (phase === PHASE.LASER_TEST) {
                const newTimes = [...laserTimes, rt];
                setLaserTimes(newTimes);

                // Debounce success click for 500ms
                state.cooldownUntil = now + 500;

                if (newTimes.length >= getRounds()) {
                    setPhase(PHASE.RESULTS);
                } else {
                    setProgress(newTimes.length + 1);
                    scheduleStimulus(rt);
                }
            }
        } else {
            handleMisclick();
        }
    };

    // Game Loop for timing visuals
    const loop = useCallback((now, dt) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const state = stateRef.current;
        const width = canvas.width;
        const height = canvas.height;

        // Clear
        ctx.clearRect(0, 0, width, height);

        let bgColor = '#222';

        if (phase === PHASE.MOUSE_TEST || phase === PHASE.LASER_TEST) {
            if (state.waitingForGreen) {
                if (now >= state.nextStimulusTime) {
                    state.waitingForGreen = false;
                    state.isGreen = true;
                    state.greenTime = now;
                    // Play sound? maybe not, visual reaction test usually silent
                }
            }

            if (state.isGreen) {
                bgColor = '#2ecc71'; // Green
            } else {
                bgColor = '#e74c3c'; // Red/Wait
            }
        } else {
            bgColor = '#333'; // Neutral
        }

        // Fill Background
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, width, height);

        // Text
        ctx.fillStyle = 'white';
        ctx.font = 'bold 40px Inter';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        if (state.isGreen) {
            ctx.fillText("CLICK!", width / 2, height / 2);
        } else if (phase === PHASE.MOUSE_TEST || phase === PHASE.LASER_TEST) {
            ctx.fillText(message, width / 2, height / 2);
            ctx.font = '20px Inter';
            ctx.fillText(`Round: ${progress} / ${getRounds()}`, width / 2, height / 2 + 60);

            // Indicator
            ctx.fillStyle = "#aaa";
            ctx.font = 'bold 24px Inter';
            const reqInput = phase === PHASE.MOUSE_TEST ? "MOUSE" : "LASER CLICKER";
            ctx.fillText(`USING: ${reqInput}`, width / 2, height - 50);
        } else {
            ctx.fillText("Read Instructions", width / 2, height / 2);
        }

    }, [phase, message, progress, mouseTimes, laserTimes]);

    useGameLoop(loop, true);

    const handleCanvasClick = (e) => {
        if (performance.now() < stateRef.current.cooldownUntil) return;

        if (phase === PHASE.MOUSE_TEST || phase === PHASE.LASER_TEST) {
            handleCalibratedClick();
        }
    };

    const getAverage = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    const mouseAvg = getAverage(mouseTimes);
    const laserAvg = getAverage(laserTimes);
    const calculatedLatency = Math.max(0, laserAvg - mouseAvg); // Don't allow negative

    const saveCalibration = () => {
        localStorage.setItem('calibratedLatency', Math.round(calculatedLatency));
        localStorage.setItem('latencyMouseAvg', mouseAvg.toFixed(2));
        localStorage.setItem('latencyLaserAvg', laserAvg.toFixed(2));
        alert(`Saved latency of ${Math.round(calculatedLatency)}ms to settings!`);

        // Update local state immediately
        setSavedStats({ mouse: mouseAvg, laser: laserAvg, diff: Math.round(calculatedLatency) });
    };

    const toggleEdit = () => {
        if (isEditing) {
            // Save manual inputs
            const m = parseFloat(manualInputs.mouse) || 0;
            const l = parseFloat(manualInputs.laser) || 0;
            const diff = Math.max(0, l - m);

            localStorage.setItem('calibratedLatency', Math.round(diff));
            localStorage.setItem('latencyMouseAvg', m.toFixed(2));
            localStorage.setItem('latencyLaserAvg', l.toFixed(2));

            setSavedStats({ mouse: m, laser: l, diff: Math.round(diff) });
            setIsEditing(false);
            alert(`Saved manual calibration: ${Math.round(diff)}ms`);
        } else {
            // Start editing
            setManualInputs({
                mouse: savedStats ? savedStats.mouse : (mouseTimes.length ? getAverage(mouseTimes).toFixed(0) : ''),
                laser: savedStats ? savedStats.laser : (laserTimes.length ? getAverage(laserTimes).toFixed(0) : '')
            });
            setIsEditing(true);
        }
    };

    const sidebarContent = (
        <>
            <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0 }}>Latency Test</h3>
                <p style={{ fontSize: '12px', color: '#aaa', marginBottom: '10px' }}>
                    Compare your mouse reaction time vs your laser clicker to calculate hardware latency.
                </p>
                <div style={{ display: 'flex', gap: '10px', fontSize: '12px', background: '#333', padding: '5px', borderRadius: '4px' }}>
                    <label style={{ cursor: 'pointer', color: lengthMode === 'short' ? '#f1c40f' : '#aaa' }}>
                        <input type="radio" checked={lengthMode === 'short'} onChange={() => setLengthMode('short')} disabled={phase !== PHASE.INTRO && phase !== PHASE.RESULTS} /> Short (5)
                    </label>
                    <label style={{ cursor: 'pointer', color: lengthMode === 'long' ? '#f1c40f' : '#aaa' }}>
                        <input type="radio" checked={lengthMode === 'long'} onChange={() => setLengthMode('long')} disabled={phase !== PHASE.INTRO && phase !== PHASE.RESULTS} /> Long (10)
                    </label>
                </div>
            </div>

            {/* Live/Saved Stats */}
            <div style={{ marginBottom: '15px', padding: '10px', background: '#333', borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, color: '#fff' }}>Stats</h4>
                    <button onClick={toggleEdit} style={{
                        fontSize: '10px', background: 'none', border: '1px solid #777', color: '#aaa', cursor: 'pointer', borderRadius: '4px'
                    }}>
                        {isEditing ? 'SAVE' : 'EDIT'}
                    </button>
                </div>

                {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#aaa', fontSize: '12px' }}>Mouse (ms):</span>
                            <input
                                type="number"
                                style={{ width: '60px', background: '#222', border: '1px solid #555', color: 'white', padding: '2px' }}
                                value={manualInputs.mouse}
                                onChange={(e) => setManualInputs({ ...manualInputs, mouse: e.target.value })}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#aaa', fontSize: '12px' }}>Laser (ms):</span>
                            <input
                                type="number"
                                style={{ width: '60px', background: '#222', border: '1px solid #555', color: 'white', padding: '2px' }}
                                value={manualInputs.laser}
                                onChange={(e) => setManualInputs({ ...manualInputs, laser: e.target.value })}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: '5px', display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#aaa' }}>Mouse Avg:</span>
                            <strong style={{ color: mouseTimes.length ? '#fff' : (savedStats ? '#aaa' : '#555') }}>
                                {mouseTimes.length ? getAverage(mouseTimes).toFixed(0) : (savedStats ? savedStats.mouse.toFixed(0) : '--')} ms
                            </strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#aaa' }}>Laser Avg:</span>
                            <strong style={{ color: laserTimes.length ? '#fff' : (savedStats ? '#aaa' : '#555') }}>
                                {laserTimes.length ? getAverage(laserTimes).toFixed(0) : (savedStats ? savedStats.laser.toFixed(0) : '--')} ms
                            </strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', borderTop: '1px solid #444', paddingTop: '5px' }}>
                            <span style={{ color: '#aaa' }}>Latency:</span>
                            <strong style={{ color: '#f1c40f' }}>
                                {savedStats ? savedStats.diff : '--'} ms
                            </strong>
                        </div>
                    </>
                )}
            </div>

            {/* Click History */}
            {(phase === PHASE.MOUSE_TEST || phase === PHASE.LASER_TEST || phase === PHASE.RESULTS) && (
                <div style={{ marginBottom: '20px', maxHeight: '300px', overflowY: 'auto' }}>
                    <h4 style={{ color: '#fff', borderBottom: '1px solid #444', paddingBottom: '5px' }}>
                        {phase.includes('mouse') ? 'Mouse' : 'Laser'} Clicks
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: '5px' }}>
                        {(phase.includes('mouse') ? mouseTimes : laserTimes).map((t, i) => (
                            <div key={i} style={{
                                fontSize: '14px', color: '#aaa', display: 'flex', justifyContent: 'space-between'
                            }}>
                                <span>#{i + 1}</span>
                                <span style={{ color: '#f1c40f' }}>{t.toFixed(0)} ms</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {phase === PHASE.RESULTS && (
                <div style={{ background: '#333', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                    <div style={{ marginBottom: '10px' }}>
                        <strong>Mouse Avg:</strong> {mouseAvg.toFixed(1)}ms
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                        <strong>Laser Avg:</strong> {laserAvg.toFixed(1)}ms
                    </div>
                    <div style={{ fontSize: '20px', color: '#f1c40f', fontWeight: 'bold' }}>
                        Diff: {calculatedLatency.toFixed(1)}ms
                    </div>
                </div>
            )}

            {phase === PHASE.RESULTS ? (
                <>
                    <button onClick={saveCalibration} style={{ background: '#2ecc71', marginBottom: '10px' }}>
                        Save & Apply
                    </button>
                    <button onClick={resetTests}>Retest</button>
                </>
            ) : (
                <button onClick={resetTests}>Reset Test</button>
            )}
        </>
    );

    return (
        <GameLayout
            sidebarContent={sidebarContent}
            canvasRef={canvasRef}
            onCanvasClick={handleCanvasClick}
            gameRunning={true}
            onQuit={() => { }} // No strict quit logic needed
        >
            {/* Modal Overlays for Instructions */}
            {phase === PHASE.INTRO && (
                <div style={modalStyle}>
                    <h1 style={{ color: '#f1c40f' }}>Latency Calibrator</h1>
                    <p>We will run two tests to determine your laser clicker&apos;s input lag.</p>
                    <ol style={{ textAlign: 'left', display: 'inline-block', maxWidth: '400px' }}>
                        <li><strong>Mouse Test:</strong> Establish your baseline reaction time.</li>
                        <li><strong>Laser Test:</strong> Measure reaction time with your device.</li>
                    </ol>
                    <br />
                    <button style={btnStyle} onClick={() => startPhase(PHASE.MOUSE_INSTR)}>Start Calibration</button>
                </div>
            )}

            {phase === PHASE.MOUSE_INSTR && (
                <div style={modalStyle}>
                    <h2>Step 1: Mouse</h2>
                    <p>Please use your <strong>Computer Mouse</strong> or Trackpad.</p>
                    <p>Click the screen as fast as possible when it turns <strong>GREEN</strong>.</p>
                    <br />
                    <button style={btnStyle} onClick={() => startPhase(PHASE.MOUSE_TEST)}>Start Mouse Test</button>
                </div>
            )}

            {phase === PHASE.LASER_INSTR && (
                <div style={modalStyle}>
                    <h2>Step 2: Laser</h2>
                    <p>Great! Now switch to your <strong>Laser Clicker</strong>.</p>
                    <p>Aim at the center and click as fast as possible when it turns <strong>GREEN</strong>.</p>
                    <br />
                    <button style={btnStyle} onClick={() => startPhase(PHASE.LASER_TEST)}>Start Laser Test</button>
                </div>
            )}

            {phase === PHASE.RESULTS && (
                <div style={modalStyle}>
                    <h2 style={{ color: '#2ecc71' }}>Calibration Complete!</h2>
                    <p>We detected an input lag of approximately:</p>
                    <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#fff', margin: '20px 0' }}>
                        {calculatedLatency.toFixed(0)} ms
                    </div>
                    <p style={{ fontSize: '14px', color: '#aaa', maxWidth: '400px', margin: '0 auto 20px' }}>
                        (Mouse: {mouseAvg.toFixed(0)}ms | Laser: {laserAvg.toFixed(0)}ms)
                    </p>
                    <button style={btnStyle} onClick={saveCalibration}>Save to Global Settings</button>
                    <br /><br />
                    <button
                        style={{ ...btnStyle, background: 'transparent', border: '1px solid #555' }}
                        onClick={resetTests}
                    >
                        Redo Test
                    </button>
                </div>
            )}
        </GameLayout>
    );
};

const modalStyle = {
    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
    background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', zIndex: 10, color: 'white', textAlign: 'center',
    padding: '20px'
};

const btnStyle = {
    padding: '12px 30px', fontSize: '18px', fontWeight: 'bold',
    background: '#3498db', color: 'white', border: 'none', borderRadius: '6px',
    cursor: 'pointer', marginBottom: '10px'
};

export default TestLatency;
